import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { POLL_UNREAD_COUNTS } from "@/lib/polling";

export interface Notification {
  id: number;
  userId: string;
  type: string;
  message: string;
  data: any;
  isRead: boolean;
  fromUserId?: string;
  createdAt: string;
}

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showPopup, setShowPopup] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);

  // Fetch notifications
  const { data: notifications = [], isLoading } = useQuery({
    // Use trailing slash to avoid 307 redirect that drops Authorization header
    queryKey: ['/api/notifications/'],
    enabled: !!user
  });

  // Fetch unread count
  const { data: unreadData } = useQuery({
    queryKey: ['/api/notifications/unread/count'],
    enabled: !!user,
    refetchInterval: POLL_UNREAD_COUNTS
  });

  const unreadCount = (unreadData as any)?.count || 0;

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to mark as read');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread/count'] });
    }
  });

  // Show popup for new notifications
  const showNotificationPopup = (notification: Notification) => {
    setCurrentNotification(notification);
    setShowPopup(true);
    
    // Auto-hide after 5 seconds (aligned with progress bar animation)
    setTimeout(() => {
      setShowPopup(false);
    }, 5000);
  };

  // Close popup
  const closePopup = () => {
    setShowPopup(false);
    if (currentNotification && !currentNotification.isRead) {
      markAsReadMutation.mutate(currentNotification.id);
    }
  };

  // Mark notification as read
  const markAsRead = (notificationId: number) => {
    markAsReadMutation.mutate(notificationId);
  };

  return {
    notifications,
    unreadCount,
    isLoading,
    showPopup,
    currentNotification,
    showNotificationPopup,
    closePopup,
    markAsRead
  };
}